import { useFormik } from 'formik'
import { useMemo } from 'react'
import { cancelarAtendimentoById } from '../../../../atendimento/core/_requests'
import {
  Button,
  FormSelect,
  FormTextArea,
  IOption,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import {
  ButtonType,
  MotivoCancelamentoAtendimentoType,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { cancelarAtendimentoSchema } from './validation/cancelarAtendimentoSchema'
import { FC } from 'react'

interface IProps {
  atendimentoId: number
  /** Chamado após cancelar com sucesso (ex.: encerrar a chamada ativa — LO-829). */
  onAtendimentoEncerrado?: () => void
}

export const CancelarAtendimentoModal: FC<IProps> = ({ atendimentoId, onAtendimentoEncerrado }) => {
  const { closeModal } = useModal()
  const { t } = useT()

  const initialValues = {
    motivo: undefined as IOption<string> | undefined,
    motivoDetalhado: '',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: useMemo(() => cancelarAtendimentoSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        // Código e detalhamento vão em campos separados: o servidor deriva a classe do motivo
        // e recusa código desconhecido, motivo de sistema e "Outros" sem detalhamento.
        const motivoCodigo = values.motivo?.value as string
        const motivoDetalhe =
          motivoCodigo === MotivoCancelamentoAtendimentoType.Outros
            ? values.motivoDetalhado.trim()
            : undefined

        await cancelarAtendimentoById(atendimentoId, motivoCodigo, motivoDetalhe)
        await showAlert({
          title: t('common.success'),
          text: 'Atendimento cancelado com sucesso',
          icon: 'success',
        })
        closeModal()
        onAtendimentoEncerrado?.()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const isOutros = formik.values.motivo?.value === MotivoCancelamentoAtendimentoType.Outros

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormSelect
        formik={formik}
        name='motivo'
        entity='motivo_cancelamento'
        fetchOptions={OptionsHelper.getOptionsMotivosNaoRealizacao}
        isDisabled={formik.isSubmitting}
        isRequired
      />

      {isOutros && (
        <FormTextArea
          formik={formik}
          name='motivoDetalhado'
          entity='detalhes_cancelamento'
          className='mt-3'
          isDisabled={formik.isSubmitting}
          rows={3}
          isRequired
        />
      )}

      <div className='d-flex justify-content-end mt-6'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.confirm')}
        </Button>
      </div>
    </form>
  )
}
